import React, {useContext, useEffect, useState} from "react";
import {I18nReactContext} from "../../../contexts/I18nContext";
// TODO: When WeezBee expands globally, import detectBrowserLanguage for auto-detection
import {defaultLanguage} from "../../../contexts/ContextLib";
import {Language} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import {map} from "lodash";
import './LoginLanguageSelector.scss';

/**
 * Language selector component specifically for login pages
 * Uses localStorage to persist user's language choice
 * 
 * @param {object} props - Component props
 * @param {string} [props.position] - Position of the selector ('top-right', 'top-left', 'bottom-right', 'bottom-left')
 * @returns {JSX.Element} The LoginLanguageSelector component
 */
export const LoginLanguageSelector = ({position = 'top-right'}) => {
    const {i18n, switchLanguage, language} = useContext(I18nReactContext);
    const [languagesOptions, setLanguagesOptions] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    /**
     * Handles click on the language button
     * 
     * @param {object} event - The click event
     */
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    /**
     * Handles closing the language menu
     */
    const handleClose = () => {
        setAnchorEl(null);
    };

    /**
     * Handles language selection and saves to localStorage
     * 
     * @param {object} params - The function parameters container object
     * @param {string} params.selectedLanguage - The selected language code
     */
    const onSelectedLanguage = ({selectedLanguage}) => {
        // Save to localStorage for login page persistence
        localStorage.setItem('weezbee_login_language', selectedLanguage);
        
        // Switch language without updating user preferences (since user is not logged in)
        switchLanguage({newSelectedLanguage: selectedLanguage});
        
        handleClose();
    };

    /**
     * Gets the current language from localStorage or defaults to Hebrew
     * Priority: localStorage > default (Hebrew)
     *
     * TODO: When WeezBee expands globally, consider using browser language auto-detection:
     * return localStorage.getItem('weezbee_login_language') || detectBrowserLanguage() || defaultLanguage;
     *
     * @returns {string} The current language code
     */
    const getCurrentLanguage = () => {
        return localStorage.getItem('weezbee_login_language') || defaultLanguage;
    };

    useEffect(() => {
        let LoginLanguageOptions = ['heIL', 'enUS', 'arIL', 'ruRU', 'ptPT']
        // Set up language options
        setLanguagesOptions(LoginLanguageOptions.map(lang => ({
            text: i18n.translate(`languages.languageKeys.${lang}`),
            value: lang,
        })));

        // Set initial language from localStorage
        const savedLanguage = getCurrentLanguage();
        if (savedLanguage && savedLanguage !== language) {
            switchLanguage({newSelectedLanguage: savedLanguage});
        }
    }, [i18n, language, switchLanguage]);

    return (
        <div className={`login-language-selector login-language-selector--${position}`}>
            <div className="language-icon-button-wrapper" onClick={handleClick}>
                <IconButton
                    size="small"
                    aria-controls={open ? 'login-language-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    className="language-icon-button"
                >
                    <Language fontSize="small"/>
                </IconButton>
                <span className="language-text">
                    {i18n.translate(`languages.languageKeys.${language}`)}
                </span>
            </div>
            <Menu
                anchorEl={anchorEl}
                id="login-language-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                slotProps={{
                    paper: {
                        elevation: 0,
                        sx: {
                            overflow: 'visible',
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                            mt: 1.5,
                        },
                    },
                }}
                transformOrigin={{horizontal: 'right', vertical: 'top'}}
                anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
            >
                <MenuItem key="title" disabled>
                    {i18n.translate('languages.label')}
                </MenuItem>
                <Divider key="divider-1"/>
                {map(languagesOptions, (option) => (
                    <MenuItem 
                        key={option.value}
                        onClick={() => onSelectedLanguage({selectedLanguage: option.value})}
                        selected={option.value === language}
                    >
                        {option.text}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}; 